import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../../global.css';
import './PatientCheckout.css';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import SearchLocationInput from '../Utils/SearchLocationInput';
import logo from '../../Assets/logo.png';
import ReactDOM from 'react-dom';

const SLOT_OPTIONS = [
    { value: '8-12', label: '8:00 AM – 12:00 PM' },
    { value: '12-16', label: '12:00 PM – 4:00 PM' },
    { value: '16-20', label: '4:00 PM – 8:00 PM' }
];

// helper to format JS Date to YYYY-MM-DD (to match Airtable-style date strings)
const formatDateToYYYYMMDD = (date) => {
    if (!(date instanceof Date)) return '';
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatToMMDDYYYY = (yyyyMmDd) => {
    if (!yyyyMmDd) return '';
    const [year, month, day] = yyyyMmDd.split('-');
    if (!year || !month || !day) return '';
    return `${month}-${day}-${year}`; // MM-DD-YYYY
};

const PatientCheckout = () => {
    const [searchParams] = useSearchParams();
    const clinicId = searchParams.get('clinic');

    const [clinic, setClinic] = useState(null);
    const [services, setServices] = useState([]);
    const [selectedServiceId, setSelectedServiceId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        patientName: '',
        patientEmail: '',
        patientPhone: '',
        patientAddress: '',
        appointmentDate: '', // will be set from calendar
        notes: '',
        marketingConsent: false
    });
    const [selectedSlot, setSelectedSlot] = useState('');
    const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showMarketingModal, setShowMarketingModal] = useState(false);
    const [marketingError, setMarketingError] = useState('');
    const [checkoutInProgress, setCheckoutInProgress] = useState(false);
    const [sessionExpiryWarning, setSessionExpiryWarning] = useState(false);
    const [sessionTimeRemaining, setSessionTimeRemaining] = useState(null); // in minutes
    const [activeCheckoutUrl, setActiveCheckoutUrl] = useState(null); // Store active checkout URL for resume button


    /**
     * Fetch clinic details and services
     */
    useEffect(() => {
        if (!clinicId) {
            setError('No clinic ID provided. Please access this page with a valid clinic ID.');
            setLoading(false);
            return;
        }

        // Check for existing checkout session
        const checkoutSession = () => {
            const existingSession = sessionStorage.getItem('checkoutSession');
            if (existingSession) {
                const sessionData = JSON.parse(existingSession);
                const sessionExpiry = new Date(sessionData.expiresAt);

                if (sessionExpiry > new Date()) {
                    // Session still valid, prevent new checkout
                    setCheckoutInProgress(true);

                    // Get the checkout URL if available
                    const storedCheckoutUrl = sessionStorage.getItem('checkoutSessionUrl');
                    if (storedCheckoutUrl) {
                        setActiveCheckoutUrl(storedCheckoutUrl);
                    }

                    setError('You have an checkout session. Please complete the payment to finalize your booking.');
                    setLoading(false);
                    return true;
                } else {
                    // Session expired, clear it
                    sessionStorage.removeItem('checkoutSession');
                    sessionStorage.removeItem('checkoutSessionUrl');
                    setActiveCheckoutUrl(null);
                    setCheckoutInProgress(false);
                    setError(null);
                    return false;
                }
            }
            return false;
        };

        // Check session on mount
        const hasActiveSession = checkoutSession();

        if (!hasActiveSession) {
            fetchClinicDetails();
        }

        // Listen for window focus to re-check session (when user returns from payment)
        const handleWindowFocus = () => {
            checkoutSession();
        };

        // Listen for pageshow to handle bfcache restore (iOS Safari back button)
        const handlePageShow = (event) => {
            if (event.persisted) {
                setLoading(false);
                checkoutSession();
            }
        };

        window.addEventListener('focus', handleWindowFocus);
        window.addEventListener('pageshow', handlePageShow);

        return () => {
            window.removeEventListener('focus', handleWindowFocus);
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, [clinicId]);

    /**
     * Monitor checkout session expiry and warn user
     */
    useEffect(() => {
        const interval = setInterval(() => {
            const existingSession = sessionStorage.getItem('checkoutSession');
            if (existingSession) {
                const sessionData = JSON.parse(existingSession);
                const sessionExpiry = new Date(sessionData.expiresAt);
                const now = new Date();
                const timeRemaining = sessionExpiry - now;
                const minutesRemaining = Math.ceil(timeRemaining / (1000 * 60));

                // Warn when 5 minutes or less remaining
                if (minutesRemaining <= 5 && minutesRemaining > 0) {
                    setSessionExpiryWarning(true);
                    setSessionTimeRemaining(minutesRemaining);
                } else if (timeRemaining <= 0) {
                    // Session expired
                    sessionStorage.removeItem('checkoutSession');
                    setCheckoutInProgress(false);
                    setSessionExpiryWarning(false);
                    setSessionTimeRemaining(null);
                    setError('Your checkout session has expired. Please start over.');
                } else {
                    // Session still valid with more than 5 minutes
                    setSessionExpiryWarning(false);
                    setSessionTimeRemaining(null);
                }
            }
        }, 30000); // Check every 30 seconds

        return () => clearInterval(interval);
    }, []);

    const handleMarketingCheckboxChange = (e) => {
        const checked = e.target.checked;

        if (!checked) {
            setFormData(prev => ({ ...prev, marketingConsent: false }));
            setMarketingError('');
            return;
        }

        // If name/email missing, show inline error and do NOT open modal
        if (!formData.patientName || !formData.patientEmail) {
            setMarketingError('Please enter your name and email before opting in.');
            setFormData(prev => ({ ...prev, marketingConsent: false }));
            return;
        }

        // Clear error and open modal
        setMarketingError('');
        setShowMarketingModal(true);
    };

    const handleMarketingAgree = async () => {
        if (!formData.patientName || !formData.patientEmail) {
            setMarketingError('Please enter your name and email before opting in.');
            return;
        }

        setFormData(prev => ({ ...prev, marketingConsent: true }));
        setShowMarketingModal(false);

        try {
            await fetch(`${process.env.REACT_APP_API_URL}/api/checkout/marketing-subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.patientName,
                    email: formData.patientEmail,
                    clinicId
                })
            });
        } catch (err) {
            console.error('Error subscribing to marketing emails:', err);
        }
    };


    const handleMarketingCancel = () => {
        setFormData(prev => ({ ...prev, marketingConsent: false }));
        setShowMarketingModal(false);
    };

    /**
     * Fetch clinic details from backend
     */
    const fetchClinicDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/checkout/clinic-details?clinic=${clinicId}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setClinic(data.clinic || null);
                setServices(data.services || []);
            } else {
                setError(data.message || 'Failed to fetch clinic details');
            }
        } catch (err) {
            console.error('Error fetching clinic details:', err);
            setError('Failed to load clinic details. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle form input changes
     */
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    /**
     * Handle calendar date selection
     */
    const selectedServiceDetails = useMemo(
        () => services.find(service => service.id === selectedServiceId),
        [services, selectedServiceId]
    );



    const handleCalendarChange = (date) => {
        const isoString = formatDateToYYYYMMDD(date);
        setSelectedCalendarDate(date);
        setFormData(prev => ({
            ...prev,
            appointmentDate: isoString
        }));
    };

    // Calculate date range for calendar (today to today + 30 days)
    const todayDate = new Date();
    const maxCalendarDate = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() + 30);

    /**
     * Handle appointment booking confirmation click - Show modal
     */
    const handleBookingClick = (e) => {
        e.preventDefault();

        if (!selectedServiceId) {
            setError('Please select a service.');
            return;
        }

        if (!selectedSlot) {
            setError('Please select a time slot.');
            return;
        }

        if (!formData.appointmentDate) {
            setError('Please select a date from the calendar.');
            return;
        }

        if (
            !formData.patientName ||
            !formData.patientEmail ||
            !formData.patientPhone ||
            !formData.patientAddress
        ) {
            setError('Please fill in all required fields.');
            return;
        }

        setError(null);
        setShowConfirmModal(true);
    };

    /**
     * Handle confirmed appointment booking
     */
    const handleConfirmBooking = async () => {
        setShowConfirmModal(false);
        setCheckoutInProgress(true);

        try {
            setLoading(true);
            setError(null);

            // Create checkout session to prevent duplicates
            const SESSION_EXPIRY_MS = 60 * 60 * 1000; // 60 minutes
            const expiresAt = new Date(Date.now() + SESSION_EXPIRY_MS);
            const checkoutSessionId = `checkout_${clinicId}_${Date.now()}`;

            // Store session in localStorage/sessionStorage
            sessionStorage.setItem('checkoutSession', JSON.stringify({
                id: checkoutSessionId,
                clinicId,
                expiresAt: expiresAt.toISOString(),
                createdAt: new Date().toISOString()
            }));

            // Build payload for the provider-matching function
            const pDateForMatcher = formatToMMDDYYYY(formData.appointmentDate); // -> MM-DD-YYYY

            const matchPayload = {
                clinicId,
                serviceId: selectedServiceId,
                p_address: formData.patientAddress,
                p_service: selectedServiceDetails?.name || '',
                p_shift: selectedSlot,
                p_date: pDateForMatcher,
                p_date_iso: formData.appointmentDate,  // ISO format for booking
                api_key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
                // Additional patient data for booking creation
                patientName: formData.patientName,
                patientEmail: formData.patientEmail,
                patientPhone: formData.patientPhone,
                appointmentDate: formData.appointmentDate
            };

            const matchResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/api/checkout/match-provider`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(matchPayload)
                }
            );

            const matchData = await matchResponse.json();

            // Check if the backend returned an error (Stripe status, etc.)
            if (!matchData.success) {
                console.warn('Booking rejected:', matchData.message);
                setError(matchData.message);
                sessionStorage.removeItem('checkoutSession');
                setCheckoutInProgress(false);
                setLoading(false);
                return;
            }

            // Store booking ID in sessionStorage for success page (if provider not found, we'll send emails)
            if (matchData.booking?.bookingId) {
                sessionStorage.setItem('lastBookingId', matchData.booking.bookingId);
                sessionStorage.setItem('bookingStatus', matchData.booking.bookingStatus);
            }

            // Reset form fields
            setFormData({
                patientName: '',
                patientEmail: '',
                patientPhone: '',
                patientAddress: '',
                appointmentDate: '',
                notes: ''
            });
            setSelectedServiceId(null);
            setSelectedSlot('');
            setSelectedCalendarDate(null);
            setSelectedLocation('');

            // Redirect to Stripe checkout if checkoutUrl is available
            if (matchData.checkoutUrl) {
                // Store the checkout URL for resume functionality
                sessionStorage.setItem('checkoutSessionUrl', matchData.checkoutUrl);
                // Keep session active during payment - Stripe will handle the transaction
                // Session will be cleared on success page or expire naturally after 60 minutes
                window.location.href = matchData.checkoutUrl;
            }
        } catch (err) {
            console.error('Error creating booking or matching provider:', err);
            setError('Failed to process booking. Please try again.');
            sessionStorage.removeItem('checkoutSession');
            setCheckoutInProgress(false);
            setLoading(false);
        }
    };


    /**
     * Handle location selection from SearchLocationInput
     */
    const handleLocationSelect = (address) => {
        setSelectedLocation(address);
        setFormData(prev => ({
            ...prev,
            patientAddress: address
        }));
    };


    if (loading && !clinic) {
        return (
            <div className="checkout-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading clinic information...</p>
                </div>
            </div>
        );
    }

    if (checkoutInProgress && error) {
        return (
            <div className="checkout-container">
                <div className="checkout-information-box">
                    <div>
                        <h2>Checkout Information</h2>
                        <p>{error}</p>
                        {checkoutInProgress && (
                            <p className="session-info">
                                Your checkout session is still active. Session expires in 60 minutes.
                            </p>
                        )}
                    </div>
                    {activeCheckoutUrl && (
                        <button
                            onClick={() => window.location.href = activeCheckoutUrl}
                            className="checkout-continue-btn"
                        >
                            Continue to Payment
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="checkout-container">
            <div className="checkout-wrapper">
                {clinic && (
                    <div className="clinic-header">
                        <div className="clinic-logo">
                            <img src={logo} alt="GetQuickCare" className="logo" />
                        </div>
                        <div className="clinic-info">
                            <h1 className="clinic-name">{clinic.name}</h1>
                            <p className="clinic-address">
                                📍 {clinic.address}
                            </p>
                            {clinic.phoneNumber && (
                                <p className="clinic-phone">
                                    📞 {clinic.phoneNumber}
                                </p>
                            )}
                            {clinic.email && (
                                <p className="clinic-email">
                                    ✉️ {clinic.email}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                <div className="checkout-content">
                    {/* Services dropdown */}
                    <div className="services-section">
                        <h2>Select a Service</h2>
                        {services.length > 0 ? (
                            <div className="service-select-wrapper">
                                <select
                                    className="service-select"
                                    value={selectedServiceId || ''}
                                    onChange={(e) => {
                                        setSelectedServiceId(e.target.value);
                                        setSelectedSlot('');
                                        setSelectedCalendarDate(null);
                                        setFormData(prev => ({
                                            ...prev,
                                            appointmentDate: ''
                                        }));
                                    }}
                                >
                                    <option value="">-- Select a Service --</option>
                                    {services.map(service => (
                                        <option key={service.id} value={service.id}>
                                            {service.name} – $
                                            {service.cost ? Number(service.cost).toFixed(2) : '0.00'}
                                        </option>
                                    ))}
                                </select>

                                {selectedServiceDetails && (
                                    <p className="selected-service-description">
                                        {selectedServiceDetails.description}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <p className="no-services">No services available at this clinic.</p>
                        )}
                    </div>

                    {/* Slot + Calendar + Booking Form */}
                    {selectedServiceDetails && selectedServiceId && (
                        <div className="booking-form-section">
                            <h2>Book Your Appointment</h2>
                            <form onSubmit={handleBookingClick} className="booking-form">
                                {/* Time Slot */}
                                <div className="form-group">
                                    <label htmlFor="slot">Time Slot *</label>
                                    <select
                                        id="slot"
                                        name="slot"
                                        value={selectedSlot}
                                        onChange={(e) => {
                                            setSelectedSlot(e.target.value);
                                            setSelectedCalendarDate(null);
                                            setFormData(prev => ({
                                                ...prev,
                                                appointmentDate: ''
                                            }));
                                        }}
                                        required
                                    >
                                        <option value="">Select a time slot</option>
                                        {SLOT_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Calendar */}
                                <div className="form-group calendar-group">
                                    <label>Select Date *</label>
                                    {selectedSlot ? (
                                        <div className="calendar-container">
                                            <Calendar
                                                onChange={handleCalendarChange}
                                                value={selectedCalendarDate}
                                                minDate={todayDate}
                                                maxDate={maxCalendarDate}
                                                maxDetail="month"
                                            />
                                        </div>
                                    ) : (
                                        <p className="no-availability-message">
                                            Please select a time slot first.
                                        </p>
                                    )}
                                </div>

                                {/* Service Summary */}
                                <div className="service-summary">
                                    <h3>Service Summary</h3>
                                    <div className="summary-item">
                                        <span>Service:</span>
                                        <span>{selectedServiceDetails.name}</span>
                                    </div>
                                    <div className="summary-item">
                                        <span>Price:</span>
                                        <span>
                                            ${Number(selectedServiceDetails.cost || 0).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="summary-item">
                                        <span>Date:</span>
                                        <span>{formData.appointmentDate || 'Not selected'}</span>
                                    </div>
                                    <div className="summary-item">
                                        <span>Time Slot:</span>
                                        <span>
                                            {selectedSlot
                                                ? SLOT_OPTIONS.find(s => s.value === selectedSlot)?.label
                                                : 'Not selected'}
                                        </span>
                                    </div>
                                </div>

                                {/* Patient Name */}
                                <div className="form-group">
                                    <label htmlFor="patientName">Full Name *</label>
                                    <input
                                        type="text"
                                        id="patientName"
                                        name="patientName"
                                        value={formData.patientName}
                                        onChange={handleInputChange}
                                        placeholder="Enter your full name"
                                        required
                                    />
                                </div>

                                {/* Patient Email (required) */}
                                <div className="form-group">
                                    <label htmlFor="patientEmail">Email Address *</label>
                                    <input
                                        type="email"
                                        id="patientEmail"
                                        name="patientEmail"
                                        value={formData.patientEmail}
                                        onChange={handleInputChange}
                                        placeholder="Enter your email"
                                        required
                                    />
                                </div>

                                {/* Patient Phone (required) */}
                                <div className="form-group">
                                    <label htmlFor="patientPhone">Phone Number *</label>
                                    <input
                                        type="tel"
                                        id="patientPhone"
                                        name="patientPhone"
                                        value={formData.patientPhone}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                                            setFormData(prev => ({
                                                ...prev,
                                                patientPhone: value
                                            }));
                                        }}
                                        placeholder="Enter your phone number (10 digits)"
                                        maxLength="10"
                                        inputMode="numeric"
                                        required
                                    />
                                </div>

                                {/* Patient Address (required, at bottom) */}
                                <div className="form-group form-group-full-width">
                                    <label>Patient Address *</label>
                                    <SearchLocationInput
                                        setSelectedLocation={handleLocationSelect}
                                        initialQuery={selectedLocation}
                                    />
                                </div>

                                {error && !checkoutInProgress && (
                                    <div style={{
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e0e0e0',
                                        borderLeft: '4px solid #2196F3',
                                        borderRadius: '6px',
                                        padding: '16px',
                                        marginBottom: '16px',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                    }}>
                                        <p style={{
                                            margin: '0 0 8px 0',
                                            color: '#424242',
                                            fontWeight: '600',
                                            fontSize: '15px'
                                        }}>
                                            {error}
                                        </p>
                                        {activeCheckoutUrl && (
                                            <p style={{
                                                margin: '0',
                                                color: '#666666',
                                                fontSize: '13px',
                                                lineHeight: '1.5'
                                            }}>
                                                Session expires in 60 minutes
                                            </p>
                                        )}
                                    </div>
                                )}
                                {activeCheckoutUrl && (
                                    <button
                                        onClick={() => window.location.href = activeCheckoutUrl}
                                        className="checkout-continue-btn"
                                        style={{ marginBottom: '16px' }}
                                    >
                                        Continue to Payment
                                    </button>
                                )}
                                {sessionExpiryWarning && (
                                    <div style={{ backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107', padding: '12px', borderRadius: '5px', marginBottom: '15px' }}>
                                        Your checkout session is expiring soon! {sessionTimeRemaining && `${sessionTimeRemaining} minute${sessionTimeRemaining !== 1 ? 's' : ''} remaining.`} Please complete your payment.
                                    </div>
                                )}
                                <div className="form-group form-group-full-width marketing-consent-row">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.marketingConsent}
                                            onChange={handleMarketingCheckboxChange}
                                            disabled={checkoutInProgress}
                                        />
                                        <span>
                                            I’d like to receive marketing emails and offers.
                                        </span>
                                    </label>
                                    {marketingError && (
                                        <div className="marketing-consent-error" role="alert">
                                            {marketingError}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    className="submit-btn"
                                    disabled={loading || checkoutInProgress}
                                >
                                    {loading ? 'Processing...' : checkoutInProgress ? 'Checkout In Progress...' : 'Book Appointment'}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Are you sure?</h2>
                        <p className="modal-message">Do you want to proceed with this booking?</p>
                        <div className="modal-buttons">
                            <button
                                className="btn-cancel"
                                onClick={() => setShowConfirmModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-confirm"
                                onClick={handleConfirmBooking}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showMarketingModal && ReactDOM.createPortal(
                (
                    <div className="modal-backdrop" onClick={handleMarketingCancel}>
                        <div className="marketing-consent-modal" onClick={(e) => e.stopPropagation()}>
                            <h3>Marketing Emails Consent</h3>
                            <div className="modal-body">
                                <p>
                                    By opting in, you agree to receive marketing and promotional
                                    communications from this clinic via email. You can unsubscribe at any
                                    time using the link in our emails.
                                </p>
                                <p>
                                    We will process your personal data in accordance with our privacy
                                    policy and applicable regulations.
                                </p>
                            </div>
                            {marketingError && (
                                <div className="error-message small">{marketingError}</div>
                            )}
                            <div className="modal-actions">
                                <button type="button" onClick={handleMarketingCancel}>
                                    Cancel
                                </button>
                                <button type="button" className="primary" onClick={handleMarketingAgree}>
                                    I Agree
                                </button>
                            </div>
                        </div>
                    </div>
                ),
                document.body
            )}
        </div>
    );
};

export default PatientCheckout;
